import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import type { QuizsetTitleSuggestionRes } from '@/api/gen/_model/quizsetTitleSuggestionRes';
import type { SearchHighlightTextRes } from '@/api/gen/_model/searchHighlightTextRes';
import { useLocale } from '@/hooks';
import { FolderIcon, MagnifyingGlassIcon } from '@/icons';
import { usePublicCategories } from '@/module/public/hooks';
import { useFindSubjectGroupCount } from '@/module/public/hooks/useFindSubjectGroupCount';
import { usePublicSearchFilterQuery } from '@/module/public/hooks/usePublicSearchFilterQuery';
import { isTitleAutocompleteSearchType, useSearchQuizQuery } from '@/module/public/hooks/useSearchQuizQuery';
import { buildSourceBackedHighlightSegments } from '@/shared/quizList/utils/buildSourceBackedHighlightSegments';
import { cn } from '@/utils/tailwind.util';
import { type CategorySearchSuggestion, getCategorySearchSuggestions } from './categorySearchSuggestions';
import { useQuizsetTitleAutocomplete } from './useQuizsetTitleAutocomplete';
interface SearchSuggestionPanelProps {
  query: string;
  eventType?: string;
  onAfterSelect: () => void;
  onAfterTitleSelect: () => void;
  onSelectTitle: (title: string) => void;
  onVisibilityChange?: (isVisible: boolean) => void;
}
export const SearchSuggestionPanel = ({
  query,
  eventType,
  onAfterSelect,
  onAfterTitleSelect,
  onSelectTitle,
  onVisibilityChange
}: SearchSuggestionPanelProps) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    locale
  } = useLocale();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const activeOptionIndexRef = useRef(-1);
  const {
    setSubject
  } = usePublicSearchFilterQuery();
  const {
    searchType
  } = useSearchQuizQuery();
  const {
    subjectGroups
  } = usePublicCategories();
  const findSubjectGroupCount = useFindSubjectGroupCount();
  const isPublicSearch = eventType === 'public';
  const canShowTitleAutocomplete = isPublicSearch && isTitleAutocompleteSearchType(searchType);
  const {
    suggestions: titleSuggestions
  } = useQuizsetTitleAutocomplete(query, canShowTitleAutocomplete);
  const categorySuggestions = useMemo(() => getCategorySearchSuggestions(subjectGroups, query), [query, subjectGroups]);
  const handleSelectCategory = useCallback((suggestion: CategorySearchSuggestion) => {
    logger.track('select_search_category_suggestion', {
      categoryName: suggestion.displayName,
      categoryGroupId: suggestion.groupId,
      categoryType: suggestion.type,
      mode: 'search_filter'
    });
    void setSubject(suggestion.groupId);
    onAfterSelect();
  }, [onAfterSelect, setSubject]);
  const handleSelectTitle = useCallback((suggestion: QuizsetTitleSuggestionRes, rank: number) => {
    logger.track('select_search_title_suggestion', {
      query,
      title: suggestion.title,
      quizsetId: suggestion.quizsetId,
      rank,
      eventType
    });
    onSelectTitle(suggestion.title);
    onAfterTitleSelect();
  }, [eventType, onAfterTitleSelect, onSelectTitle, query]);
  const hasCategorySuggestions = categorySuggestions.length > 0;
  const hasTitleSuggestions = titleSuggestions.length > 0;
  const suggestionCount = categorySuggestions.length + titleSuggestions.length;
  const shouldShowPanel = hasCategorySuggestions || hasTitleSuggestions;
  const setActiveOption = useCallback((index: number) => {
    activeOptionIndexRef.current = index;
    setActiveOptionIndex(index);
  }, []);
  useEffect(() => {
    setActiveOption(-1);
  }, [query, setActiveOption]);
  useEffect(() => {
    onVisibilityChange?.(shouldShowPanel);
  }, [onVisibilityChange, shouldShowPanel]);
  const categoryLabel = String(t('common:category.subject'));
  const titleLabel = String(t('common:public.search.titleSuggestion'));
  const quizCountFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const formatSuggestionQuizCount = useCallback((count: number | string | undefined) => {
    if (typeof count !== 'number') return '';
    return String(t('common:formatNumber.count', {
      n: quizCountFormatter.format(count)
    }));
  }, [quizCountFormatter, t]);
  const selectSuggestionByIndex = useCallback((index: number) => {
    if (index < 0 || index >= suggestionCount) return;
    if (index < categorySuggestions.length) {
      handleSelectCategory(categorySuggestions[index]);
      return;
    }
    const titleSuggestionIndex = index - categorySuggestions.length;
    handleSelectTitle(titleSuggestions[titleSuggestionIndex], titleSuggestionIndex + 1);
  }, [categorySuggestions, handleSelectCategory, handleSelectTitle, suggestionCount, titleSuggestions]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (suggestionCount === 0) return;
      if (event.key === 'ArrowDown' || event.code === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = activeOptionIndexRef.current < 0 ? 0 : (activeOptionIndexRef.current + 1) % suggestionCount;
        setActiveOption(nextIndex);
        optionRefs.current[nextIndex]?.scrollIntoView?.({
          block: 'nearest'
        });
        return;
      }
      if (event.key === 'ArrowUp' || event.code === 'ArrowUp') {
        event.preventDefault();
        const nextIndex = activeOptionIndexRef.current < 0 ? suggestionCount - 1 : (activeOptionIndexRef.current - 1 + suggestionCount) % suggestionCount;
        setActiveOption(nextIndex);
        optionRefs.current[nextIndex]?.scrollIntoView?.({
          block: 'nearest'
        });
        return;
      }
      if (event.key === 'Enter' && activeOptionIndexRef.current >= 0) {
        event.preventDefault();
        selectSuggestionByIndex(activeOptionIndexRef.current);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        onAfterTitleSelect();
      }
    };
    const searchForm = panelRef.current?.closest('form');
    const searchInput = searchForm?.elements.namedItem('searchText');
    const keyDownTarget = searchInput instanceof HTMLElement ? searchInput : searchForm;
    keyDownTarget?.addEventListener('keydown', handleKeyDown);
    return () => keyDownTarget?.removeEventListener('keydown', handleKeyDown);
  }, [onAfterTitleSelect, selectSuggestionByIndex, setActiveOption, suggestionCount]);
  if (!shouldShowPanel) {
    return null;
  }
  return <div ref={panelRef} className={cn('absolute left-0 right-0 top-[calc(100%-2px)] z-20', 'rounded-b-sm border-2 border-t-0 border-strong bg-default p-xs shadow-md')} role="listbox" aria-label={String(t('common:public.search.autoComplete'))} data-sentry-component="SearchSuggestionPanel" data-sentry-source-file="SearchSuggestionPanel.tsx">
      {hasCategorySuggestions && <ul className={'flex flex-col gap-none'}>
          {categorySuggestions.map((suggestion, index) => <li key={suggestion.groupId}>
              <button ref={element => {
          optionRefs.current[index] = element;
        }} type="button" aria-label={`${suggestion.displayName} ${categoryLabel}`} aria-selected={activeOptionIndex === index} role="option" className={cn('flex min-h-[56px] w-full items-center gap-sm rounded-2xs px-sm py-xs text-left', 'hover:bg-neutral focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-400', activeOptionIndex === index && 'bg-neutral')} onMouseDown={e => e.preventDefault()} onMouseEnter={() => setActiveOption(index)} onClick={() => handleSelectCategory(suggestion)}>
                <FolderIcon size={'md'} className={'shrink-0 text-neutral max-tablet-sm:!size-[16px]'} />
                <span className={'flex min-w-0 flex-1 flex-col gap-4xs'}>
                  <span className={'text-caption-lg-semibold text-assistive'}>
                    {categoryLabel}
                  </span>
                  <span className={'truncate text-body-md-semibold text-default'}>
                    <HighlightedName name={getCategoryDisplayPath(suggestion)} query={query.trim()} matchedName={suggestion.matchedName} />
                  </span>
                </span>
                <span className={'shrink-0 text-caption-lg-semibold text-alternative'}>
                  {formatSuggestionQuizCount(findSubjectGroupCount(suggestion.groupId, suggestion.type === 'sub_subject'))}
                </span>
              </button>
            </li>)}
        </ul>}

      {hasTitleSuggestions && <div className={cn(hasCategorySuggestions && 'mt-xs border-t pt-xs')}>
          <ul className={'flex flex-col gap-none'}>
            {titleSuggestions.map((suggestion, index) => {
          const optionIndex = categorySuggestions.length + index;
          return <li key={suggestion.quizsetId}>
                  <button ref={element => {
              optionRefs.current[optionIndex] = element;
            }} type="button" aria-label={`${suggestion.title} ${titleLabel}`} aria-selected={activeOptionIndex === optionIndex} role="option" className={cn('flex min-h-[44px] w-full items-center gap-sm rounded-2xs px-sm py-xs text-left text-body-md-semibold text-default', 'hover:bg-neutral focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-400', activeOptionIndex === optionIndex && 'bg-neutral')} onMouseDown={e => e.preventDefault()} onMouseEnter={() => setActiveOption(optionIndex)} onClick={() => handleSelectTitle(suggestion, index + 1)}>
                    <MagnifyingGlassIcon size={'sm'} className={'shrink-0 text-neutral max-tablet-sm:!size-[16px]'} />
                    <span className={'min-w-0 truncate'}>
                      <HighlightedName name={suggestion.title} query={query.trim()} matchedName={suggestion.title} highlightSegments={suggestion.titleHighlights} />
                    </span>
                  </button>
                </li>;
        })}
          </ul>
        </div>}
    </div>;
};
const getCategoryDisplayPath = (suggestion: CategorySearchSuggestion) => {
  if (suggestion.type !== 'sub_subject') return suggestion.displayName;
  const matchedIndex = suggestion.displayName.lastIndexOf(suggestion.matchedName);
  if (matchedIndex < 0) return suggestion.displayName;
  const subjectName = suggestion.displayName.slice(0, matchedIndex).trim();
  return subjectName ? `${subjectName} > ${suggestion.matchedName}` : suggestion.displayName;
};
const HighlightedName = ({
  name,
  query,
  matchedName,
  highlightSegments
}: {
  name: string;
  query: string;
  matchedName: string;
  highlightSegments?: readonly SearchHighlightTextRes[];
}) => {
  const sourceBackedSegments = buildSourceBackedHighlightSegments(name, highlightSegments);
  if (sourceBackedSegments) {
    return <>
        {sourceBackedSegments.map((segment, index) => segment.type === 'hit' ? <span key={`${segment.value}-${index}`} className={'text-[#5748f2]'}>
              {segment.value}
            </span> : <span key={`${segment.value}-${index}`} className={'text-default'}>
              {segment.value}
            </span>)}
      </>;
  }
  const shouldUseCategoryFallback = matchedName.trim().toLocaleLowerCase() !== name.trim().toLocaleLowerCase();
  if (!shouldUseCategoryFallback) return <>{name}</>;
  const matchIndex = getCategoryFallbackMatchIndex(name, query, matchedName);
  const highlightLength = matchIndex >= 0 ? getCategoryFallbackHighlightLength(name, query) : 0;
  if (!query || matchIndex < 0 || highlightLength === 0) return <>{name}</>;
  const before = name.slice(0, matchIndex);
  const match = name.slice(matchIndex, matchIndex + highlightLength);
  const after = name.slice(matchIndex + highlightLength);
  return <>
      {before && <span className={'text-default'}>{before}</span>}
      <span className={'text-[#5748f2]'}>{match}</span>
      {after && <span className={'text-default'}>{after}</span>}
    </>;
};
const getCategoryFallbackMatchIndex = (name: string, query: string, matchedName: string) => {
  if (!query.trim()) return -1;
  const directMatchIndex = name.toLocaleLowerCase().indexOf(query.toLocaleLowerCase());
  if (directMatchIndex >= 0) return directMatchIndex;
  return name.toLocaleLowerCase().indexOf(matchedName.toLocaleLowerCase());
};
const getCategoryFallbackHighlightLength = (name: string, query: string) => {
  const directMatchIndex = name.toLocaleLowerCase().indexOf(query.toLocaleLowerCase());
  if (directMatchIndex >= 0) return query.length;
  return 0;
};